import { useEffect } from "react";
import { analytics } from "@/lib/analytics";
import {
  normalizeErrorName,
  sanitizeErrorMessage,
  sanitizeErrorStack,
  sanitizePathname,
} from "@/lib/analytics/error-utils";
import { captureGlobalError } from "@/lib/sentry";

export const useGlobalErrorTracking = () => {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const error = event.error as Error | undefined;
      captureGlobalError(error || event.message, "Window Error", {
        error_name: normalizeErrorName(error),
        line: event.lineno,
        column: event.colno,
        route_path: sanitizePathname(window.location.pathname),
      });
      analytics.trackError("Window Error", sanitizeErrorMessage(error?.message ?? event.message), {
        error_name: normalizeErrorName(error),
        error_stack: sanitizeErrorStack(error?.stack),
        line: event.lineno,
        column: event.colno,
        route_path: sanitizePathname(window.location.pathname),
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as Error | string | undefined;
      const message = reason instanceof Error ? reason.message : reason;
      captureGlobalError(reason || message, "Unhandled Promise Rejection", {
        error_name: normalizeErrorName(reason),
        route_path: sanitizePathname(window.location.pathname),
      });
      analytics.trackError("Unhandled Promise Rejection", sanitizeErrorMessage(message), {
        error_name: normalizeErrorName(reason),
        error_stack:
          reason instanceof Error ? sanitizeErrorStack(reason.stack) : sanitizeErrorStack(reason),
        route_path: sanitizePathname(window.location.pathname),
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);
};
